import React, { useState } from 'react';
import { CheckCircle, Shield, Sparkles, TrendingUp, Crown, ChevronDown, Mail, ArrowRight } from 'lucide-react';
import { MultiStepForm } from '../components/Trial/MultiStepForm';
import { SEOHead } from '../components/SEO/SEOHead';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/AcquireFooter';
export function FreeTrialPage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistData, setWaitlistData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const handleMultiStepSuccess = () => {
    setShowSuccessModal(true);
  };
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingWaitlist(true);
    try {
      const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12781423/uk8px0y/';
      // Create form data instead of JSON
      const formData = new URLSearchParams();
      formData.append('name', waitlistData.name);
      formData.append('email', waitlistData.email);
      formData.append('phone', waitlistData.phone);
      formData.append('timestamp', new Date().toISOString());
      formData.append('source', 'Website');
      const response = await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });
      if (!response.ok) {
        throw new Error('Webhook submission failed');
      }
      setWaitlistSubmitted(true);
      setWaitlistData({
        name: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Waitlist signup error:', error);
      // Still show success for better UX
      setWaitlistSubmitted(true);
      setWaitlistData({
        name: '',
        email: '',
        phone: ''
      });
    } finally {
      setIsSubmittingWaitlist(false);
    }
  };
  return <>
      <SEOHead title="Join Beta - Lock In 50% Off For Life | AcquireFlow" description="Join AcquireFlow's exclusive beta program. $199 one-time fee for lifetime 50% discount on all plans, 45-day trial, priority access, and free 1-on-1 setup call." canonical="https://acquireflow.com/trial" data-id="element-2585" />

      <div className="min-h-screen bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212]" data-id="element-2586">
        <Navbar data-id="element-2587" />

        <div className="relative pt-20 pb-20" data-id="element-2588">
          {/* Floating Particles Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-2589">
            {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#3ab795] rounded-full opacity-20" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float-particle ${15 + Math.random() * 15}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }} data-id="element-2590" />)}
          </div>

          <div className="max-w-[1400px] mx-auto px-6 md:px-8" data-id="element-2591">
            {/* Compact Hero Section */}
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 relative z-10" data-id="element-2592">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full mb-6 backdrop-blur-sm animate-fade-in" data-id="element-2593">
                <Crown className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2594" />
                <span className="text-sm font-medium text-[#3ab795] uppercase tracking-wide" data-id="element-2595">
                  Limited Beta Access • $199
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.1] tracking-tight animate-fade-in" style={{
              animationDelay: '100ms'
            }} data-id="element-2596">
                Join AcquireFlow Beta{' '}
                <span className="text-[#3ab795] relative inline-block" data-id="element-2597">
                  Lock In 50% Off For Life
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[#3ab795]/30 rounded-full" data-id="element-2598"></span>
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-base md:text-lg lg:text-xl text-[#b8bcc8] leading-relaxed mb-8 animate-fade-in" style={{
              animationDelay: '200ms'
            }} data-id="element-2599">
                Get 45 days to test AcquireFlow and save $6,000+/year with
                lifetime beta pricing.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-3 animate-fade-in" style={{
              animationDelay: '300ms'
            }} data-id="element-2600">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-lg text-sm font-medium text-[#3ab795]" data-id="element-2601">
                  <Sparkles className="w-4 h-4" strokeWidth={2.5} data-id="element-2602" />
                  <span data-id="element-2603">Limited spots</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white" data-id="element-2604">
                  <TrendingUp className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2605" />
                  <span data-id="element-2606">Lifetime 50% discount</span>
                </div>
              </div>
            </div>

            {/* MAIN FOCUS: Centered Form */}
            <div className="max-w-2xl mx-auto relative z-10" data-id="element-2607">
              <div className="relative animate-fade-in" style={{
              animationDelay: '400ms'
            }} data-id="element-2608">
                {/* White glow effect */}
                <div className="absolute inset-0 rounded-3xl blur-3xl bg-white opacity-30" style={{
                transform: 'scale(0.92)'
              }} data-id="element-2609" />

                <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/10 rounded-2xl shadow-[0_20px_60px_rgba(255,255,255,0.1)] p-8 sm:p-12 transition-all duration-300 hover:border-white/20 hover:shadow-[0_24px_80px_rgba(255,255,255,0.15)]" data-id="element-2610">
                  {/* Form Header */}
                  <div className="text-center mb-8 pb-6 border-b border-white/10" data-id="element-2611">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-2612">
                      Secure Your Beta Spot
                    </h2>
                    <p className="text-base md:text-lg text-[#b8bcc8]" data-id="element-2613">
                      Join the exclusive group shaping AcquireFlow's future
                    </p>
                  </div>

                  <MultiStepForm onSuccess={handleMultiStepSuccess} data-id="element-2614" />
                </div>
              </div>

              {/* Waitlist Section */}
              <div className="mt-8 pt-6 border-t border-white/10" data-id="element-2615">
                <button onClick={() => setShowWaitlist(!showWaitlist)} className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 group" data-id="element-2616">
                  <span className="text-[#b8bcc8] text-sm md:text-base" data-id="element-2617">
                    Not ready to join beta? Join our waitlist instead
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#b8bcc8] transition-transform duration-200 ${showWaitlist ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-2618" />
                </button>

                {showWaitlist && <div className="mt-4 p-6 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-lg animate-fade-in" data-id="element-2619">
                    {!waitlistSubmitted ? <>
                        <div className="flex items-center gap-2 mb-3" data-id="element-2620">
                          <Mail className="w-5 h-5 text-[#3ab795]" strokeWidth={2} data-id="element-2621" />
                          <h3 className="text-white font-semibold text-lg" data-id="element-2622">
                            Join Launch Waitlist
                          </h3>
                        </div>
                        <p className="text-sm text-[#b8bcc8] mb-4" data-id="element-2623">
                          Get notified when we launch publicly. No beta benefits
                          or lifetime discount included.
                        </p>
                        <form onSubmit={handleWaitlistSubmit} className="space-y-3" data-id="element-2624">
                          <input type="text" value={waitlistData.name} onChange={e => setWaitlistData({
                      ...waitlistData,
                      name: e.target.value
                    })} placeholder="Full Name" required disabled={isSubmittingWaitlist} className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2625" />
                          <input type="email" value={waitlistData.email} onChange={e => setWaitlistData({
                      ...waitlistData,
                      email: e.target.value
                    })} placeholder="your@email.com" required disabled={isSubmittingWaitlist} className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2626" />
                          <input type="tel" value={waitlistData.phone} onChange={e => setWaitlistData({
                      ...waitlistData,
                      phone: e.target.value
                    })} placeholder="Phone Number" required disabled={isSubmittingWaitlist} className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 rounded-lg text-white placeholder-[#b8bcc8]/50 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2627" />
                          <button type="submit" disabled={isSubmittingWaitlist} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2628">
                            {isSubmittingWaitlist ? <>
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2629">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2630"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2631"></path>
                                </svg>
                                <span data-id="element-2632">Joining...</span>
                              </> : <>
                                <span data-id="element-2633">Join Waitlist</span>
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} data-id="element-2634" />
                              </>}
                          </button>
                        </form>
                      </> : <div className="text-center py-4" data-id="element-2635">
                        <div className="w-12 h-12 bg-[#3ab795]/10 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-2636">
                          <CheckCircle className="w-6 h-6 text-[#3ab795]" strokeWidth={2} data-id="element-2637" />
                        </div>
                        <h3 className="text-white font-semibold mb-1" data-id="element-2638">
                          You're on the list!
                        </h3>
                        <p className="text-sm text-[#b8bcc8]" data-id="element-2639">
                          We'll notify you when we launch publicly.
                        </p>
                      </div>}
                  </div>}
              </div>

              {/* Terms */}
              <p className="mt-6 text-center text-sm text-[#b8bcc8]/70" data-id="element-2640">
                By signing up, you agree to our{' '}
                <a href="/terms-and-conditions" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2641">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy-policy" className="text-[#3ab795] hover:text-[#2d9f7e] underline transition-colors" data-id="element-2642">
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>

        <Footer data-id="element-2643" />
      </div>

      {/* Success Modal - Beta Version */}
      {showSuccessModal && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" data-id="element-2644">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 rounded-2xl p-8 md:p-10 max-w-md w-full text-center shadow-2xl" data-id="element-2645">
            <div className="w-20 h-20 bg-[#3ab795]/10 rounded-full flex items-center justify-center mx-auto mb-6 relative" data-id="element-2646">
              <div className="absolute inset-0 bg-[#3ab795] rounded-full animate-ping opacity-20" data-id="element-2647"></div>
              <Crown className="w-10 h-10 text-[#3ab795] relative z-10" strokeWidth={2} data-id="element-2648" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-2649">
              Welcome to the Beta! 🎉
            </h3>
            <p className="text-[#b8bcc8] mb-6 text-lg" data-id="element-2650">Check your email for:</p>
            <div className="text-left space-y-2 mb-8 bg-white/5 rounded-lg p-4" data-id="element-2651">
              <div className="flex items-center gap-2 text-[#b8bcc8]" data-id="element-2652">
                <CheckCircle className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2653" />
                <span className="text-sm" data-id="element-2654">Beta access credentials</span>
              </div>
              <div className="flex items-center gap-2 text-[#b8bcc8]" data-id="element-2655">
                <CheckCircle className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2656" />
                <span className="text-sm" data-id="element-2657">Setup call scheduling link</span>
              </div>
              <div className="flex items-center gap-2 text-[#b8bcc8]" data-id="element-2658">
                <CheckCircle className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-2659" />
                <span className="text-sm" data-id="element-2660">Getting started guide</span>
              </div>
            </div>
            <button onClick={() => setShowSuccessModal(false)} className="w-full px-6 py-4 bg-[#3ab795] text-white font-semibold rounded-xl hover:bg-[#2d9f7e] transition-all duration-300 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#0a0a0a]" data-id="element-2661">
              Got it
            </button>
          </div>
        </div>}
    </>;
}